export type IdentityFieldSource =
  | 'profile'
  | 'authentication'
  | 'company'
  | 'organization'
  | 'business_profile'
  | 'none';

export interface ResolvedIdentityField {
  value: string;
  source: IdentityFieldSource;
}

interface IdentityCandidate {
  value?: string | null;
  source: Exclude<IdentityFieldSource, 'none'>;
}

export interface PersonIdentityInput {
  userId?: string | null;
  profile?: {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    department?: string | null;
  } | null;
  authentication?: {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
  } | null;
  businessProfile?: {
    contactPerson?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

export interface CurrentPerson {
  id: string | null;
  firstName: ResolvedIdentityField;
  lastName: ResolvedIdentityField;
  email: ResolvedIdentityField;
  phone: ResolvedIdentityField;
  department: ResolvedIdentityField;
  hasSavedDetails: boolean;
}

export interface OrganisationIdentityInput {
  company?: {
    id?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    industry?: string | null;
    businessType?: string | null;
    companySize?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    stateProvince?: string | null;
    postalCode?: string | null;
    country?: string | null;
    taxId?: string | null;
  } | null;
  organization?: {
    id?: string | null;
    name?: string | null;
    domain?: string | null;
    subscriptionTier?: string | null;
  } | null;
  businessProfile?: {
    companyName?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    industry?: string | null;
    businessType?: string | null;
    companySize?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    stateProvince?: string | null;
    postalCode?: string | null;
    country?: string | null;
    taxId?: string | null;
  } | null;
}

export interface CurrentOrganisation {
  companyId: string | null;
  organizationId: string | null;
  name: ResolvedIdentityField;
  email: ResolvedIdentityField;
  phone: ResolvedIdentityField;
  website: ResolvedIdentityField;
  industry: ResolvedIdentityField;
  businessType: ResolvedIdentityField;
  companySize: ResolvedIdentityField;
  addressLine1: ResolvedIdentityField;
  addressLine2: ResolvedIdentityField;
  city: ResolvedIdentityField;
  stateProvince: ResolvedIdentityField;
  postalCode: ResolvedIdentityField;
  country: ResolvedIdentityField;
  taxId: ResolvedIdentityField;
  domain: ResolvedIdentityField;
  subscriptionTier: string | null;
  hasSavedDetails: boolean;
}

const clean = (value?: string | null) => value?.trim() || '';

const resolveField = (...candidates: IdentityCandidate[]): ResolvedIdentityField => {
  const match = candidates.find((candidate) => clean(candidate.value));

  return match
    ? { value: clean(match.value), source: match.source }
    : { value: '', source: 'none' };
};

const splitContactName = (contactPerson?: string | null) => {
  const parts = clean(contactPerson).split(/\s+/).filter(Boolean);

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

export const composeCurrentPerson = (input: PersonIdentityInput): CurrentPerson => {
  const businessName = splitContactName(input.businessProfile?.contactPerson);
  const firstName = resolveField(
    { value: input.profile?.firstName, source: 'profile' },
    { value: input.authentication?.firstName, source: 'authentication' },
    { value: businessName.firstName, source: 'business_profile' },
  );
  const lastName = resolveField(
    { value: input.profile?.lastName, source: 'profile' },
    { value: input.authentication?.lastName, source: 'authentication' },
    { value: businessName.lastName, source: 'business_profile' },
  );
  const email = resolveField(
    { value: input.profile?.email, source: 'profile' },
    { value: input.authentication?.email, source: 'authentication' },
    { value: input.businessProfile?.email, source: 'business_profile' },
  );
  const phone = resolveField(
    { value: input.profile?.phone, source: 'profile' },
    { value: input.authentication?.phone, source: 'authentication' },
    { value: input.businessProfile?.phone, source: 'business_profile' },
  );
  const department = resolveField({
    value: input.profile?.department,
    source: 'profile',
  });

  return {
    id: input.userId || null,
    firstName,
    lastName,
    email,
    phone,
    department,
    hasSavedDetails: [firstName, lastName, email, phone].some((field) => Boolean(field.value)),
  };
};

export const composeCurrentOrganisation = (
  input: OrganisationIdentityInput,
): CurrentOrganisation => {
  const name = resolveField(
    { value: input.company?.name, source: 'company' },
    { value: input.organization?.name, source: 'organization' },
    { value: input.businessProfile?.companyName, source: 'business_profile' },
  );
  const email = resolveField(
    { value: input.company?.email, source: 'company' },
    { value: input.businessProfile?.email, source: 'business_profile' },
  );
  const phone = resolveField(
    { value: input.company?.phone, source: 'company' },
    { value: input.businessProfile?.phone, source: 'business_profile' },
  );
  const website = resolveField(
    { value: input.company?.website, source: 'company' },
    { value: input.businessProfile?.website, source: 'business_profile' },
  );
  const industry = resolveField(
    { value: input.company?.industry, source: 'company' },
    { value: input.businessProfile?.industry, source: 'business_profile' },
  );
  const businessType = resolveField(
    { value: input.company?.businessType, source: 'company' },
    { value: input.businessProfile?.businessType, source: 'business_profile' },
  );
  const companySize = resolveField(
    { value: input.company?.companySize, source: 'company' },
    { value: input.businessProfile?.companySize, source: 'business_profile' },
  );
  const addressLine1 = resolveField(
    { value: input.company?.addressLine1, source: 'company' },
    { value: input.businessProfile?.addressLine1, source: 'business_profile' },
  );
  const addressLine2 = resolveField(
    { value: input.company?.addressLine2, source: 'company' },
    { value: input.businessProfile?.addressLine2, source: 'business_profile' },
  );
  const city = resolveField(
    { value: input.company?.city, source: 'company' },
    { value: input.businessProfile?.city, source: 'business_profile' },
  );
  const stateProvince = resolveField(
    { value: input.company?.stateProvince, source: 'company' },
    { value: input.businessProfile?.stateProvince, source: 'business_profile' },
  );
  const postalCode = resolveField(
    { value: input.company?.postalCode, source: 'company' },
    { value: input.businessProfile?.postalCode, source: 'business_profile' },
  );
  const country = resolveField(
    { value: input.company?.country, source: 'company' },
    { value: input.businessProfile?.country, source: 'business_profile' },
  );
  const taxId = resolveField(
    { value: input.company?.taxId, source: 'company' },
    { value: input.businessProfile?.taxId, source: 'business_profile' },
  );
  const domain = resolveField({ value: input.organization?.domain, source: 'organization' });

  return {
    companyId: input.company?.id || null,
    organizationId: input.organization?.id || null,
    name,
    email,
    phone,
    website,
    industry,
    businessType,
    companySize,
    addressLine1,
    addressLine2,
    city,
    stateProvince,
    postalCode,
    country,
    taxId,
    domain,
    subscriptionTier: input.organization?.subscriptionTier || null,
    hasSavedDetails: Boolean(
      name.value || email.value || phone.value || website.value || addressLine1.value || postalCode.value,
    ),
  };
};
